import type { EngineContext } from "../../context";
import { XMLBuilder } from "fast-xml-parser";
import { resolveIrsIntakeTaxYear, schemaVersionForTaxYear, softwareIdForTaxYear } from "../../constants/taxYear";
import { buildSplitRecipientDetail } from "../../constants/recipientIdentity";
import { Form1099SAData } from "./interface/1099-SA-interface";
import {
  generateRandomRecordId,
  generateUniqueSubmissionId,
  generateUniqueTransmissionId,
} from "../../constants/genRecordId";
import {parseAmount, stateAndTerritories} from "../../constants/validationFunctions";
import {
  buildTransmitterGroup,
  buildVendorGroup,
  buildIssuerDetail,
} from "../../xml/transmitterData";
import { v4 as uuidv4 } from "uuid";

// Define totals object with the same structure as the INT version
const totals = {
  GrossDistributionAmt: 0,
  ExcessContributionsEarningsAmt: 0,
  FMVOnDateOfDeathAmt: 0,
};

export async function generate1099SAXml(
  ctx: EngineContext,records: Form1099SAData[]) {
  if (!records || !Array.isArray(records) || records.length === 0) {
    throw new Error("No records found or records input is invalid.");
  }

  // Fetch the transmitter data
  const transmitterData = ctx.transmitter as any;

  // Initialize the XML builder with the same settings as 1099-INT
  const builder = new XMLBuilder({
    ignoreAttributes: false,
    format: true,
    suppressEmptyNode: true,
    attributeNamePrefix: "@_",
    processEntities: true,
  });

  // Determine tax year from the first record, consistent with 1099-INT approach
  const taxYear = resolveIrsIntakeTaxYear(records[0], ctx.irs.maxIntakeTaxYear);
  // const taxYear = records[0]["1099_YEAR"] || "2023";

  // Build an array of form details, one for each record, matching 1099-INT structure
  const form1099SADetails = records.map((record) => {
    // Validate and force a valid state code for US addresses, same as 1099-INT
    const validState =
        record.RECIPIENT_STATE &&
        stateAndTerritories.includes(record.RECIPIENT_STATE.toUpperCase())
            ? record.RECIPIENT_STATE.toUpperCase()
            : "IL";

    return {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsd": "http://www.w3.org/2001/XMLSchema",
      TaxYr: taxYear.toString(),
      RecordId: (record.id).toString(),
      VoidInd: "0",
      CorrectedInd: record.corrected ? "1" : "0",
      // TY2026 layout-V2 records file via the shared split builder (business as
      // BusinessName+BUSINESS_TIN); legacy records keep the shape below.
      RecipientDetail: buildSplitRecipientDetail(
        record,
        ctx.irs.testIndicator
            ? "000000000"
            : record.BOX_RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN_ADDRESS",
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: record.RECIPIENT_CITY || "UNKNOWN_CITY",
            StateAbbreviationCd: validState,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        }
      ) ?? {
        TIN: ctx.irs.testIndicator
            ? "000000000"
            : record.BOX_RECIPIENT_ID_NUMBER.replace(/-/g, ""),
        TINSubmittedTypeCd: "INDIVIDUAL_TIN",
        PersonNameControlTxt: record.RECIPIENT_NAME.trim()
            .substring(0, 4)
            .toUpperCase(),
        PersonName: {
          PersonFirstNm: record.RECIPIENT_NAME,
          PersonLastNm: record.RECIPIENT_NAME2 || "Unknown",
        },
        MailingAddressGrp: {
          USAddress: {
            AddressLine1Txt: record.RECIPIENT_ADDRESS1 || "UNKNOWN_ADDRESS",
            ...(record.RECIPIENT_ADDRESS2 && {
              AddressLine2Txt: record.RECIPIENT_ADDRESS2,
            }),
            CityNm: record.RECIPIENT_CITY || "UNKNOWN_CITY",
            StateAbbreviationCd: validState,
            ZIPCd: record.RECIPIENT_ZIP,
          },
        },
      },
      ...(record.BOX_ACCOUNT_NUMBER && {
        RecipientAccountNum: record.BOX_ACCOUNT_NUMBER,
      }),
      GrossDistributionAmt: parseAmount(record.BOX1_GROSS_DISTRIBUTION || "0"),
      ExcessContributionsEarningsAmt: parseAmount(
          record.BOX2_EARNING_ON_EXCESS || "0"
      ),
      Form1099SADistributionCd: record.BOX3_DISTRIBUTION_CODE.toString(),
      FMVOnDateOfDeathAmt: parseAmount(record.BOX4_FMV_ON_DATE_OF_DEATH || "0"),
      HSAInd: record.BOX5_HSA ? "1" : "0",
      ArcherMSAInd: record.BOX5_ARCHER_MSA ? "1" : "0",
      MAMSAInd: record.BOX5_MA_MSA ? "1" : "0",
    };
  });

  // Generate unique identifiers
  const uniqueTransmissionId = generateUniqueTransmissionId(ctx.irs.tcc);
  const submissionId = generateUniqueSubmissionId();

  // Build the complete envelope structure matching 1099-INT exactly
  const xmlStructure = {
    "?xml": {
      "@_version": "1.0",
      "@_encoding": "UTF-8",
    },
    IRTransmission: {
      "@_xmlns": "urn:us:gov:treasury:irs:ir",
      "@_xmlns:xsi": "http://www.w3.org/2001/XMLSchema-instance",
      "@_xsi:schemaLocation":
          "urn:us:gov:treasury:irs:ir IRS-IRIntakeTransmissionMessage.xsd",
      IRTransmissionManifest: {
        SchemaVersionNum: schemaVersionForTaxYear(taxYear),
        UniqueTransmissionId: uniqueTransmissionId,
        TaxYr: taxYear,
        PriorYearDataInd: transmitterData.priorYearDataIndicator || "0",
        TransmissionTypeCd: "O",
        TestCd: transmitterData.testFileIndicator || "T",
        TransmitterGrp: buildTransmitterGroup(transmitterData),
        VendorCd: transmitterData.vendorIndicator || "V",
        SoftwareId: softwareIdForTaxYear(taxYear, ctx.irs),
        VendorGrp: buildVendorGroup(transmitterData),
        TotalIssuerFormCnt: 1,
        TotalRecipientFormCnt: form1099SADetails.length.toString(),
        PaperSubmissionInd: "0",
        MediaSourceCd: "M",
        SubmissionChannelCd: "A2A",
      },
      IRSubmission1Grp: [
        {
          IRSubmission1Header: {
            SubmissionId: submissionId,
            TaxYr: taxYear,
            IssuerDetail: buildIssuerDetail(transmitterData),
            ContactPersonInformationGrp: {
              ...(transmitterData.contactName && {
                ContactPersonNm: transmitterData.contactName,
              }),
              ...(transmitterData.contactTelephoneNumber && {
                ContactPhoneNum: transmitterData.contactTelephoneNumber,
              }),
              ...(transmitterData.contactEmailAddress && {
                ContactEmailAddressTxt: transmitterData.contactEmailAddress,
              }),
              ...(transmitterData.ContactFaxNum && {
                ContactFaxNum: transmitterData.ContactFaxNum,
              }),
            },
            FormTypeCd: "1099SA",
            ParentFormTypeCd: "1096",
            CFSFElectionInd: "0",
            JuratSignatureGrp: {
              SignatureIntentInd: "1",
              JuratSignaturePIN: "00000",
              SignatureDt: "2023-04-04",
              ...(transmitterData.contactName && {
                JuratPersonTitleTxt: "CFO",
              }),
              ...(transmitterData.contactName && {
                PersonNm: transmitterData.contactName,
              }),
            },
            TotalReportedRcpntFormCnt: form1099SADetails.length.toString(),
            IRSubmission1FormTotals: {
              Form1099SATotalAmtGrp: {
                // Use fixed values as 1099-INT does to avoid calculation errors
                GrossDistributionAmt: "100",
                ExcessContributionsEarningsAmt: "0",
                FMVOnDateOfDeathAmt: "0",
              },
            },
          },
          IRSubmission1Detail: {
            Form1099SADetail: form1099SADetails,
          },
        },
      ],
    },
  };

  try {
    const xml = builder.build(xmlStructure);
    return { xml, taxYear };
  } catch (error: any) {
    console.error("Error generating XML:", error);
    throw new Error(`Failed to generate XML: ${error.message}`);
  }
}